import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useMcAccessStatus } from '@/hooks/useMcAccessStatus';
import { useReauth } from '@/contexts/ReauthContext';
import type { OptimizationJob } from '@/types/optimization';
import type { QuotaCapInfo } from '@/components/enforcement/QuotaLimitDialog';
import { useAsyncJob, type CancelJobResult } from './useAsyncJob';
import { trackConversion } from '@/lib/analytics';
import i18n from '@/lib/i18n';
import { numberLocale } from '@/lib/language';
import { requestDesktopNotificationPermission } from '@/lib/desktop-notifications';
import {
  OPTIMIZATION_SUCCESS_EVENT,
  type OptimizationSuccessEventDetail,
} from './useJobCompletionNotifications';
import { useCancelCleanupLock } from './useCancelCleanupLock';
import {
  OPTIMIZATION_RUN_MARKER_TTL_MS,
  clearOptimizationRunMarker,
  getOptimizationRunMarker,
  markOptimizationRunning,
  subscribeToOptimizationRunMarker,
  type OptimizationRunMarker,
} from '@/lib/crossTabOptimizationLock';

// Funnel-Guard (Bericht 8.3 Punkt 6): optimization_complete darf pro Job nur
// einmal feuern. Der Completion-Effect haengt am Polling-Uebergang
// isOptimizing true -> false; ein kurzes Flackern der Active-Job-Query (oder
// eine zweite Hook-Instanz) wuerde denselben Job sonst doppelt melden.
// Modul-Scope statt useRef, damit der Guard Instanzen und Remounts ueberlebt.
const trackedCompletedJobIds = new Set<string>();

export type OptimizeOpts = {
  optimizationType: string;
  isAdvanced: boolean;
  selectAllMode: { enabled: boolean; estimatedCount: number } | null;
  advancedSelectedIds: Set<string>;
  advancedFilters: Record<string, string>;
  hasUnoptimizedProducts: boolean;
  totalProductCount?: number;
  /**
   * Wie viele der angeforderten Produkte NEU sind (noch nicht optimiert bzw.
   * `failed`) — die einzige Menge, die unter dem Bestandsmass Kapazitaet
   * belegt. Wiederholungen belegen keinen zusaetzlichen betreuten Platz und
   * laufen deshalb auch bei vollem Konto durch.
   *
   * Optional, und das ist der Punkt: nur der Feed-Check-Pfad KENNT diese Zahl
   * (er waehlt gezielt `not_optimized`). Alle anderen Einstiege lassen sie
   * `undefined` — dann verzichten Gate und Vorwarnung hier bewusst auf eine
   * Aussage und ueberlassen sie dem Server, der die Auswahl ohnehin selbst
   * aufteilt. Eine geratene Zahl haette Wiederholungslaeufe gesperrt, die der
   * Server anstandslos annimmt.
   */
  newProductCount?: number;
};

export function useOptimizationJob(feedId: string | null, feedProductCount: number = 0) {
  const { user } = useAuth();
  const { toast } = useNotifications();
  const {
    canOptimize,
    // `count` ist seit dem Bestandsmass der BETREUTE Bestand, nicht der
    // Lifetime-Verbrauch — deshalb heisst er hier managedCount.
    count: managedCount,
    limit: planLimit,
    remainingCapacity,
    isLoading: enforcementLoading,
    refetch: refetchEnforcement,
  } = useEnforcement();
  const { isAccessRevoked: isMcAccessRevoked, mcName: revokedMcName } = useMcAccessStatus();
  const { triggerReauth } = useReauth();
  const queryClient = useQueryClient();
  // Fuer die Add-on-CTA im LANGUAGE_LIMIT_REACHED-Toast (PlanBandManager
  // sitzt unter /settings?tab=subscription).
  const navigate = useNavigate();

  const [localJobInfo, setLocalJobInfo] = useState<{ jobId: string; totalProducts: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelTriggered, setCancelTriggered] = useState(false);
  const cleanupLock = useCancelCleanupLock(feedId, {
    triggered: cancelTriggered,
    feedProductCount,
    userId: user?.id ?? null,
  });
  const [optimizationJustCompleted, setOptimizationJustCompleted] = useState(false);
  const [scoreJobInfo, setScoreJobInfo] = useState<{ jobId: string; totalProducts: number } | null>(null);
  const [lastJobStats, setLastJobStats] = useState<{ completed: number; failed: number }>({ completed: 0, failed: 0 });
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showReoptimizeDialog, setShowReoptimizeDialog] = useState(false);
  const [pendingOptimizeOpts, setPendingOptimizeOpts] = useState<OptimizeOpts | null>(null);
  // Wie viele NEUE Produkte der Server beim REOPTIMIZE_CONFIRM_REQUIRED-Lauf
  // bereits abgeschnitten hat, weil sie nicht mehr in den freien Bestand
  // passten (`serverBody.dropped_new`). Der Feed-Deckel selbst zaehlt still
  // weiter und sperrt nicht mehr — eine Restzahl zu diesem Deckel gibt es
  // seit dem Bestandsmass nicht mehr zu melden.
  const [reoptimizeDroppedNew, setReoptimizeDroppedNew] = useState(0);
  const [showQuotaCapDialog, setShowQuotaCapDialog] = useState(false);
  const [quotaCapInfo, setQuotaCapInfo] = useState<QuotaCapInfo | null>(null);
  // Persists "we just completed an optimization in this session" so the modal
  // keeps rendering Success after activeJob disappears from the active-jobs query.
  // Without this the modal would briefly flip to phase=null between completion
  // and the user closing the modal, showing 0/0 with empty stepper.
  const [hasCompletedInSession, setHasCompletedInSession] = useState(false);
  // Snapshot of the completed job — needed for the success scoreboard because
  // activeJob falls out of the active-jobs query (filter status IN
  // ['queued','processing']) once the job completes, leaving SuccessScoreboard
  // with nothing to render.
  const [completedJobSnapshot, setCompletedJobSnapshot] = useState<OptimizationJob | null>(null);

  // Konvergenz 4, Befund 3(b): solange diese Instanz einen Lauf GESEHEN hat,
  // darf ein einzelner verschluckter Poll (anon-Leerantwort nach Laptop-Sleep,
  // transienter Null) das Polling nicht endgueltig beenden. trackedJobId haelt
  // das refetchInterval am Leben, bis der Nachfetch einen echten
  // Terminal-Status bestaetigt (oder Feed-Wechsel/Abbruch aufraeumen).
  const [trackedJobId, setTrackedJobId] = useState<string | null>(null);

  // Konvergenz 4, Befund 1: Cross-Tab-Marker eines Laufs, den ein ANDERER Tab
  // desselben Browsers gestartet hat. Dient als Klick-Guard und als
  // forcePolling-Quelle, damit dieser Tab den Lauf uebernimmt. Deckt nur
  // denselben Browser — der serverseitige Per-Feed-Guard steht noch aus
  // (bewusste Entscheidung fuer Dennis, siehe lib/crossTabOptimizationLock).
  const [crossTabRun, setCrossTabRun] = useState<OptimizationRunMarker | null>(null);

  // Clear stale state when feed changes
  useEffect(() => {
    setScoreJobInfo(null);
    setHasCompletedInSession(false);
    setCompletedJobSnapshot(null);
    setShowReoptimizeDialog(false);
    setPendingOptimizeOpts(null);
    setReoptimizeDroppedNew(0);
    setTrackedJobId(null);
  }, [feedId]);

  // Open the Success-Dialog when a desktop-notification click fires the
  // completion CustomEvent for this feed.
  useEffect(() => {
    if (!feedId) return;
    const handler = (evt: Event) => {
      const detail = (evt as CustomEvent<OptimizationSuccessEventDetail>).detail;
      if (!detail || detail.feedId !== feedId) return;
      setLastJobStats({ completed: detail.completed, failed: detail.failed });
      setShowProgressModal(true);
    };
    window.addEventListener(OPTIMIZATION_SUCCESS_EVENT, handler);
    return () => window.removeEventListener(OPTIMIZATION_SUCCESS_EVENT, handler);
  }, [feedId]);

  const {
    activeJob,
    cancelJob,
    invalidate,
  } = useAsyncJob<OptimizationJob>({
    tableName: 'optimization_jobs',
    channelName: 'simple-job-updates',
    queryKeyPrefix: 'simple-active-job',
    feedId,
    // Drei Quellen halten das Polling am Leben, obwohl die Query null liefert:
    // der frisch gestartete Lauf (localJobInfo), ein in dieser Instanz bereits
    // gesehener Lauf (trackedJobId — Befund 3: ein transienter null-Poll darf
    // das Tracking nicht beenden) und ein von einem anderen Tab gemeldeter
    // Lauf (crossTabRun — Befund 1: der fremde Tab uebernimmt den Lauf).
    forcePolling: !!localJobInfo || !!trackedJobId || !!crossTabRun,
  });

  // Clear localJobInfo when real job arrives from query
  useEffect(() => {
    if (activeJob && localJobInfo) {
      setLocalJobInfo(null);
    }
  }, [activeJob, localJobInfo]);

  // Jeder gesehene aktive Job wird getrackt, bis sein Terminal-Status
  // BESTAETIGT ist (Nachfetch im Completion-Effect, Abbruch, Feed-Wechsel).
  useEffect(() => {
    if (activeJob) {
      setTrackedJobId(activeJob.id);
    }
  }, [activeJob]);

  // Cross-Tab-Marker lesen + auf storage-Events reagieren: startet ein anderer
  // Tab desselben Browsers einen Lauf auf diesem Feed, invalidiert dieser Tab
  // seine Active-Job-Query und uebernimmt den Lauf (CTAs sperren, Fortschritt
  // zeigen). Wird der Marker entfernt (Terminal), laeuft der naechste Poll und
  // der Completion-Uebergang feuert hier normal.
  useEffect(() => {
    setCrossTabRun(feedId ? getOptimizationRunMarker(feedId) : null);
    if (!feedId) return;
    return subscribeToOptimizationRunMarker(feedId, (marker) => {
      setCrossTabRun(marker);
      invalidate();
    });
  }, [feedId, invalidate]);

  // TTL-Ablauf des Markers erzeugt KEIN storage-Event — ohne diesen Timer
  // wuerde ein verwaister Marker (startender Tab geschlossen, Job vom
  // 10-Minuten-Watchdog gefailt) das forcePolling dieses Tabs nie freigeben.
  useEffect(() => {
    if (!crossTabRun || !feedId) return;
    const remaining = crossTabRun.ts + OPTIMIZATION_RUN_MARKER_TTL_MS - Date.now();
    const timer = setTimeout(() => {
      setCrossTabRun(getOptimizationRunMarker(feedId));
    }, Math.max(0, remaining) + 1_000);
    return () => clearTimeout(timer);
  }, [crossTabRun, feedId]);

  const isOptimizing = !!activeJob || !!localJobInfo;

  // Track the job ID so we can fetch final stats after it disappears from the active query
  const lastJobIdRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (activeJob) {
      lastJobIdRef.current = activeJob.id;
    }
  }, [activeJob]);

  // Defense-in-depth: useOptimizationJob is called from the page AND from
  // OptimizationProgressModal as separate hook instances, so a state reset in
  // handleOptimize only clears the parent. When a fresh activeJob arrives in
  // the modal's instance while it still holds a stale completion snapshot
  // (typical after an external cancel + new optimization), wipe the success
  // state so Phase 1 renders correctly instead of replaying the old summary.
  useEffect(() => {
    if (
      activeJob &&
      activeJob.status === 'processing' &&
      (hasCompletedInSession || completedJobSnapshot)
    ) {
      setHasCompletedInSession(false);
      setCompletedJobSnapshot(null);
      setOptimizationJustCompleted(false);
    }
  }, [activeJob?.id, activeJob?.status]);

  // Track completion — fetches final stats from DB to avoid stale polling data.
  // The active query filters by status IN ['queued','processing'], so when the job
  // completes between polls, the last polled snapshot may be stale (e.g. 80/90).
  const prevIsOptimizing = useRef(isOptimizing);
  useEffect(() => {
    if (prevIsOptimizing.current && !isOptimizing) {
      setOptimizationJustCompleted(true);
      if (!cancelledRef.current && lastJobIdRef.current) {
        const jobId = lastJobIdRef.current;
        // Fetch the full completed job row — used by the SuccessScoreboard
        // (since activeJob falls out of the active-jobs query post-completion).
        // We gate hasCompletedInSession on the DB status: when the job was
        // cancelled externally (DB write, watchdog, admin action) cancelledRef
        // never gets set, and we'd otherwise show a success modal for a
        // cancelled job — see the William-Bechtgott "popup says done, then a
        // fresh optimization is running" report.
        supabase
          .from('optimization_jobs')
          .select('*')
          .eq('id', jobId)
          .single()
          .then(({ data }) => {
            if (!data) return;
            const isTerminal =
              data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled';
            if (isTerminal) {
              // Terminal bestaetigt: Tracking beenden (sonst pollte
              // trackedJobId endlos weiter) und den Cross-Tab-Marker dieses
              // Laufs entfernen — jobId-scoped, damit ein inzwischen
              // gestarteter neuer Lauf seinen Marker behaelt.
              setTrackedJobId((prev) => (prev === jobId ? null : prev));
              clearOptimizationRunMarker(data.feed_id, jobId);
            }
            // Konvergenz 4, Befund 7: ein komplett fehlgeschlagener Lauf
            // (status='failed', z.B. vom 10-Minuten-Watchdog gekillt) hatte
            // KEINEN Endzustand — das offene Modal blieb fuer immer auf
            // "Optimierung startet" stehen. Jetzt: Modal zu, ehrlicher Toast
            // mit Handlung (erneut starten — die CTAs sind wieder frei).
            if (data.status === 'failed') {
              setShowProgressModal(false);
              toast({
                title: i18n.t('dashboard:toasts.optimizationRunFailedTitle'),
                description: i18n.t('dashboard:toasts.optimizationRunFailedDesc'),
                variant: 'destructive',
              });
              return;
            }
            // Harter Gate auf den echten Terminal-Erfolg: der isOptimizing-
            // Uebergang true->false feuert auch bei LAUFENDEM Job (Feed-Wechsel
            // setzt den Query-Key um, ein verschluckter Poll-Fehler liefert
            // null), und der Nachfetch liest dann status='processing'. Ohne
            // dieses Gate oeffnete sich das Erfolgs-Modal mit Teilzaehlern —
            // auf der Seite des NEUEN Feeds, fuer einen Lauf, der noch laeuft.
            // cancelled bleibt wie bisher still (eigener Pfad).
            if (data.status !== 'completed') return;
            setHasCompletedInSession(true);
            setCompletedJobSnapshot(data as OptimizationJob);
            const completed = data.completed_products || 0;
            const failed = data.failed_products || 0;
            // Funnel-Event: derselbe completed-Gate wie oben; der Set-Guard
            // verhindert Doppel-Meldungen bei Query-Flackern/zweiter Instanz.
            if (!trackedCompletedJobIds.has(jobId)) {
              trackedCompletedJobIds.add(jobId);
              trackConversion('optimization_complete', {
                product_count: completed,
                failed_count: failed,
              });
            }
            if (completed > 0 || failed > 0) {
              setLastJobStats({ completed, failed });
              setShowProgressModal(true);
              refetchEnforcement();
            }
          });
      }
      // Invalidate product queries so the UI shows fresh data without reload
      queryClient.invalidateQueries({ queryKey: ['simple-products'] });
      queryClient.invalidateQueries({ queryKey: ['simple-product-stats'] });
      queryClient.invalidateQueries({ queryKey: ['advanced-products'] });         // AdvancedModePanel
      queryClient.invalidateQueries({ queryKey: ['total-products'] });
      queryClient.invalidateQueries({ queryKey: ['workbench-products'] });        // list-view + score-filtered
      queryClient.invalidateQueries({ queryKey: ['workbench-review-page'] });     // card-view buffer (P0-1 rename)
      queryClient.invalidateQueries({ queryKey: ['workbench-server-total'] });    // Workbench total count
      queryClient.invalidateQueries({ queryKey: ['workbench-pending-count'] });   // Workbench pending badge
      // K1-Befund: der Kommentar hier versprach 'fresh data without reload',
      // aber der sechste Werkbank-Schluessel fehlte — die Reiterzahlen zeigten
      // nach einem Optimierungslauf 30 s lang den alten Stand neben frischer Liste.
      queryClient.invalidateQueries({ queryKey: ['workbench-status-counts'] });
      cancelledRef.current = false;
      lastJobIdRef.current = null;
      const timer = setTimeout(() => setOptimizationJustCompleted(false), 500);
      return () => clearTimeout(timer);
    }
    prevIsOptimizing.current = isOptimizing;
  }, [isOptimizing]);

  // Progress data — during retry phase (retry_count > 0), hide failures from the user.
  // Failed products have been reset to 'pending' in the backend, so they appear as pending again.
  const isRetrying = (activeJob?.retry_count ?? 0) > 0;
  const progressData = activeJob
    ? {
        total: activeJob.total_products,
        completed: activeJob.completed_products || 0,
        failed: isRetrying ? 0 : (activeJob.failed_products || 0),
        pending: activeJob.total_products - (activeJob.completed_products || 0) - (isRetrying ? 0 : (activeJob.failed_products || 0)),
        queuedBatches: activeJob.queued_batches || 0,
        processedBatches: activeJob.processed_batches || 0,
        isRetrying,
      }
    : localJobInfo
    ? {
        total: localJobInfo.totalProducts,
        completed: 0,
        failed: 0,
        pending: localJobInfo.totalProducts,
        isRetrying: false,
      }
    : null;

  type Phase = 'sortieren' | 'texte' | 'attribute' | 'scoring' | 'completed' | null;

  const currentPhase: Phase = (() => {
    if (!activeJob) return localJobInfo ? 'sortieren' : null;
    const total = activeJob.total_products;
    if (activeJob.sortieren_completed_products < total) return 'sortieren';
    if (activeJob.texte_completed_products < total) return 'texte';
    if (activeJob.attribute_completed_products < total) return 'attribute';
    if (activeJob.status === 'completed' && scoreJobInfo) return 'scoring';
    if (activeJob.status === 'completed') return 'completed';
    return 'attribute'; // edge case: counters all match but status not yet 'completed'
  })();

  const secondsBetween = (start: string | null, end: string | null): number | undefined => {
    if (!start || !end) return undefined;
    return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  };

  const phaseTimings = {
    sortieren: secondsBetween(activeJob?.sortieren_started_at ?? null, activeJob?.sortieren_completed_at ?? null),
    texte: secondsBetween(activeJob?.texte_started_at ?? null, activeJob?.texte_completed_at ?? null),
    attribute: secondsBetween(activeJob?.attribute_started_at ?? null, activeJob?.attribute_completed_at ?? null),
  };

  const categoryCounts: Record<string, number> = activeJob?.sortieren_category_counts ?? {};
  const preScore: number | null = activeJob?.pre_optimization_avg_score ?? null;

  const heartbeatStaleSeconds: number | null = activeJob?.heartbeat_at
    ? Math.floor((Date.now() - new Date(activeJob.heartbeat_at).getTime()) / 1000)
    : null;

  // Der Titel-Ticker ist weg, samt seiner 3-Sekunden-Abfrage.
  //
  // Er las waehrend JEDES Laufs alle 3 s die 30 juengsten `optimized_title` des
  // FEEDS (`.eq('feed_id')`, sortiert nach `last_optimized_at`) — also dieselbe
  // feed-weite Groesse, die als "Gerade fertig" in Phase 2 und als
  // Lebenszeit-Summe im Attribut-Panel entfernt wurde. Angezeigt hat sie
  // niemand: `recentOptimizedTitles` hatte seit dem Umbau auf das
  // Zwei-Stufen-Modal keinen Leser mehr, `RollingTitles` haengt nur noch am
  // Sync-Fortschritt (`FetchProgressDialog`), und `OptimizationProgress.tsx`
  // wird von keiner Datei importiert.
  //
  // Stehenlassen war trotzdem keine Option: die Abfrage lief alle 3 s gegen
  // dieselbe Datenbank, die waehrend eines Laufs 20 Worker bedient, und beim
  // naechsten Verdrahten haette sie sofort wieder ein fremdes Produkt als
  // Ertrag DIESES Laufs gezeigt. Der Lauf-Bezug fuer genau diese Anzeige liegt
  // in `optimization_events.job_id` (siehe `phases/Phase2Texte.tsx`).

  // Optimize handler
  const handleOptimize = async (opts: OptimizeOpts, confirmOpts?: { confirm?: boolean; confirmQuotaCap?: boolean }) => {
    if (!feedId || !user?.id) return;

    if (cleanupLock.locked) {
      toast({
        title: i18n.t('dashboard:toasts.cleanupInProgress'),
        description: i18n.t('dashboard:toasts.cleanupTryAgainIn', { seconds: cleanupLock.secondsRemaining }),
      });
      return;
    }

    // Cross-Tab-Guard (Konvergenz 4, Befund 1): laeuft laut Marker in einem
    // ANDEREN Tab dieses Browsers bereits ein Lauf auf diesem Feed, wird kein
    // zweiter gestartet — stattdessen uebernimmt dieser Tab den laufenden Job
    // (invalidate + forcePolling via crossTabRun). Nur derselbe Browser; der
    // serverseitige Per-Feed-Guard steht aus (Entscheidung fuer Dennis).
    if (!isOptimizing) {
      const marker = getOptimizationRunMarker(feedId);
      if (marker) {
        // Vor dem Sperren den Job-Status verifizieren: der Provider lebt nur
        // auf der Dashboard-Seite — endet ein Lauf, waehrend keine Instanz
        // gemountet ist, raeumt den Marker niemand ab. Ein blinder Block
        // haette den Feed dann bis zu 15 Minuten (TTL) faelschlich gesperrt.
        const { data: markerJob, error: markerError } = await supabase
          .from('optimization_jobs')
          .select('id, status')
          .eq('id', marker.jobId)
          .maybeSingle();
        const stillRunning = markerJob?.status === 'queued' || markerJob?.status === 'processing';
        // Fail-closed bei Verifikationsfehler: lieber einmal zu viel sperren
        // als einen zweiten Parallel-Lauf ueber dieselben Produkte starten.
        if (stillRunning || markerError) {
          toast({ title: i18n.t('optimization:feedCheck.cta.blockedRunning') });
          setCrossTabRun(marker);
          invalidate();
          return;
        }
        // Lauf ist terminal (oder Zeile weg): stale Marker abraeumen, normal
        // weitermachen.
        clearOptimizationRunMarker(feedId, marker.jobId);
        setCrossTabRun(null);
      }
    }

    // GMC-access guard (defense-in-depth — UI buttons are already disabled).
    // Mirrors the server-side guard in enqueue-optimization-batches that returns
    // 409 GMC_ACCESS_REVOKED. Bails before showing the progress modal.
    if (isMcAccessRevoked) {
      toast({
        title: i18n.t('dashboard:toasts.mcRevokedTitle'),
        description: revokedMcName
          ? i18n.t('dashboard:toasts.optimizeMcRevokedNamed', { name: revokedMcName })
          : i18n.t('dashboard:toasts.optimizeMcRevoked'),
        variant: 'destructive',
      });
      return;
    }

    // Kapazitaets-Gate, aber NUR wenn der Lauf erkennbar NEUE Produkte enthaelt.
    // Unter dem Bestandsmass belegt eine Wiederholung keinen zusaetzlichen
    // betreuten Platz — der Server nimmt sie auch bei vollem Konto an. Ein
    // pauschales `!canOptimize` haette genau diese Laeufe hier abgefangen und
    // dem Nutzer ein Upgrade angeboten, das er fuer sein Vorhaben gar nicht
    // braucht. Kenner der Neu-Zahl ist heute allein der Feed-Check-Pfad.
    const knownNew = opts.newProductCount;
    if (!canOptimize && knownNew !== undefined && knownNew > 0) {
      setShowUpgradeModal(true);
      return;
    }

    // Kapazitaets-Vorwarnung: es passen weniger NEUE Produkte in den freien
    // Bestand als der Lauf mitbringt. Der Server (enqueue-optimization-batches)
    // wuerde die neuen Produkte still auf die freie Kapazitaet kuerzen — also
    // vorher fragen, statt den letzten Platz auf einer beliebigen Teilmenge zu
    // verbrauchen. Uebersprungen auf jedem Bestaetigungspfad, waehrend die
    // Enforcement-Daten noch laden (die Vorgabewerte wuerden falsch warnen) und
    // wenn die Neu-Zahl unbekannt ist (dann gibt es nichts zu vergleichen).
    if (
      !confirmOpts?.confirm &&
      !confirmOpts?.confirmQuotaCap &&
      !enforcementLoading &&
      knownNew !== undefined
    ) {
      // `>=` statt `>` (Bericht 8.3 Punkt 8): auch bei exaktem Gleichstand
      // (z.B. 200 neue Produkte treffen genau 200 freie Plaetze) muss der Dialog
      // warnen — der Lauf belegt die GESAMTE verbleibende Kapazitaet.
      // QuotaLimitDialog zeigt fuer diesen Fall eine eigene Copy-Variante.
      if (remainingCapacity > 0 && knownNew >= remainingCapacity) {
        setQuotaCapInfo({
          requested: knownNew,
          remaining: remainingCapacity,
          limit: planLimit,
          used: managedCount,
        });
        setPendingOptimizeOpts(opts);
        setShowQuotaCapDialog(true);
        return;
      }
    }

    // Ask for desktop-notification permission at the same user-gesture that
    // starts the job. Fire-and-forget — never blocks the job.
    requestDesktopNotificationPermission();

    setSubmitting(true);
    try {
      const useServerSelection = opts.isAdvanced ? (opts.selectAllMode?.enabled || false) : true;
      const body: Record<string, any> = {
        optimization_type: opts.isAdvanced ? opts.optimizationType : 'both,attributes',
        user_id: user.id,
        feed_id: feedId,
        use_server_selection: useServerSelection,
      };

      if (confirmOpts?.confirm) {
        body.confirm_reoptimize = true;
      }

      if (opts.isAdvanced && !opts.selectAllMode?.enabled && opts.advancedSelectedIds.size > 0) {
        body.product_ids = Array.from(opts.advancedSelectedIds);
        body.use_server_selection = false;
      } else if (!opts.isAdvanced) {
        body.filters = opts.hasUnoptimizedProducts ? { optimizationStatus: 'not_optimized' } : {};
      } else if (opts.isAdvanced && opts.selectAllMode?.enabled) {
        const activeFilters: Record<string, string> = {};
        if (opts.advancedFilters.optimizationStatus !== 'all') activeFilters.optimizationStatus = opts.advancedFilters.optimizationStatus;
        if (opts.advancedFilters.availability !== 'all') activeFilters.availability = opts.advancedFilters.availability;
        if (opts.advancedFilters.brand !== 'all') activeFilters.brand = opts.advancedFilters.brand;
        if (opts.advancedFilters.search) activeFilters.search = opts.advancedFilters.search;
        body.filters = activeFilters;
      }

      // Reset the "we just finished" flag and stale completed-job snapshot so a
      // new run doesn't open the modal already in success state. The progress
      // modal is opened only AFTER the server confirms the run (success branch),
      // so the re-optimization confirm/limit dialogs don't flash the modal first.
      setHasCompletedInSession(false);
      setCompletedJobSnapshot(null);

      const { data, error } = await supabase.functions.invoke('enqueue-optimization-batches', { body });
      if (error) {
        // Defense-in-depth: useEnforcement defaults canOptimize to `true` while the
        // initial check_can_optimize RPC is still pending, so a RED user's first
        // click after page-load slips past the local guard and hits the server.
        // The server returns 403 { error: 'Limit reached', ... } — catch that
        // here and route to the upgrade modal instead of a raw error toast.
        let serverBody: any = null;
        const ctx = (error as any)?.context;
        if (ctx) {
          if (typeof ctx.json === 'function') {
            try { serverBody = await ctx.json(); } catch { /* not JSON */ }
          } else if (typeof ctx === 'object') {
            serverBody = ctx;
          }
        }

        // Re-optimization guard: identische Auswahl wie letzter Lauf → Bestätigungs-Popup.
        // Die Antwort traegt seit dem Bestandsmass KEINE Restzahl zum
        // Feed-Deckel mehr (der zaehlt still weiter, sperrt aber nicht) — es
        // waere also nichts da, was `max`/`reoptimization_count` noch lesen
        // koennten. `dropped_new` ist etwas anderes: wie viele NEUE Produkte
        // dieses Laufs bereits am Kapazitaets-Limit abgeschnitten wurden.
        // Fehlt das Feld (aeltere Deployments, reiner Wiederholungslauf ohne
        // Neuanteil), bleibt der Hinweis im Dialog schlicht unsichtbar.
        if (serverBody?.error === 'REOPTIMIZE_CONFIRM_REQUIRED') {
          setPendingOptimizeOpts(opts);
          setReoptimizeDroppedNew(serverBody.dropped_new ?? 0);
          setShowReoptimizeDialog(true);
          return;
        }

        // Kapazitaet erschoepft: der betreute Bestand fuellt das Tarif-Limit,
        // fuer NEUE Produkte ist kein Platz mehr. `'Limit reached'` ist der
        // Vorgaengercode derselben Lage und bleibt hier stehen, solange
        // in-flight-Antworten aelterer Deployments auflaufen koennen.
        // `remaining_capacity` aus dem Fehler-Body wird bewusst NICHT angezeigt:
        // in dieser Antwort ist es strukturell 0 und damit keine Information.
        if (serverBody?.error === 'CAPACITY_REACHED' || serverBody?.error === 'Limit reached') {
          setShowUpgradeModal(true);
          refetchEnforcement();
          return;
        }

        // Sprach-Limit (Preisumbau Baender, 403): der Tarif deckt N
        // Zielsprachen, dieser Feed laege auf einer weiteren. KEIN
        // Upgrade-Modal — das spraeche ueber Produkt-Kapazitaet, die hier gar
        // nicht knapp ist. Stattdessen ein verstaendlicher Toast mit
        // Add-on-CTA zu den Abo-Einstellungen (PlanBandManager). Die Felder
        // kommen aus languageLimitReachedResponse (language-gate.ts):
        // allowed_languages (number), feed_language (z.B. 'fr').
        if (serverBody?.error === 'LANGUAGE_LIMIT_REACHED') {
          const allowedLanguages =
            typeof serverBody.allowed_languages === 'number' &&
            Number.isFinite(serverBody.allowed_languages)
              ? serverBody.allowed_languages
              : 1;
          const feedLanguage =
            typeof serverBody.feed_language === 'string' && serverBody.feed_language.trim() !== ''
              ? serverBody.feed_language.trim().toUpperCase()
              : null;
          toast({
            title: i18n.t('enforcement:languageLimit.title'),
            // Ohne feed_language (defensive: der Code ist neu, aber ein
            // umgebauter Server koennte das Feld verlieren) faellt die Copy
            // auf die Fassung ohne Sprachnennung zurueck, statt "auf null"
            // zu behaupten. `count` steuert die Pluralform der Zielsprachen.
            description: feedLanguage
              ? i18n.t('enforcement:languageLimit.description', {
                  count: allowedLanguages,
                  feedLanguage,
                })
              : i18n.t('enforcement:languageLimit.descriptionNoLanguage', {
                  count: allowedLanguages,
                }),
            variant: 'destructive',
            action: {
              label: i18n.t('enforcement:languageLimit.cta'),
              onClick: () => navigate('/settings?tab=subscription'),
            },
            // Laengeres Fenster als die Sonner-Default (~4s): der Toast
            // traegt die einzige Handlung (Add-on buchen), die braucht Zeit.
            durationMs: 12_000,
          });
          return;
        }

        // Fair-Use-Fenster erschoepft: kein Upgrade-Fall, sondern eine
        // Wartezeit. Der Platz fuellt sich von selbst wieder auf, sobald die
        // aeltesten Laeufe aus dem 30-Tage-Fenster fallen — deshalb ein Hinweis
        // statt des Upgrade-Modals, und ein Datum, wenn der Server eines nennt.
        if (serverBody?.error === 'FAIR_USE_REACHED') {
          // Ein unparsbares Datum wird wie gar keines behandelt. `new Date()`
          // wirft nicht, sondern liefert eine Invalid Date, und
          // `toLocaleDateString()` darauf ergibt woertlich "Invalid Date" —
          // das stuende dann als Termin in der Meldung.
          const parsed = serverBody.fair_use_resets_at ? new Date(serverBody.fair_use_resets_at) : null;
          const resets = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
          toast({
            title: i18n.t('enforcement:fairUse.title'),
            description: resets
              // Locale explizit, wie an jeder anderen Datumsstelle in src/
              // (ExperimentCard, WorkbenchProductCard, AdsPerformance …): ohne
              // Argument nimmt toLocaleDateString die Browser-Sprache, nicht die
              // eingestellte UI-Sprache — die deutsche Oberflaeche haette dann
              // "9/1/2026" gezeigt.
              ? i18n.t('enforcement:fairUse.descriptionWithDate', {
                  date: resets.toLocaleDateString(numberLocale(i18n.language)),
                })
              : i18n.t('enforcement:fairUse.description'),
            variant: 'destructive',
          });
          return;
        }

        // Die Auswahl war leer (alles schon optimiert, Filter trifft nichts).
        // Kein Fehler im eigentlichen Sinne, deshalb kein destructive-Toast.
        if (serverBody?.error === 'NOTHING_TO_OPTIMIZE') {
          toast({
            title: i18n.t('enforcement:nothingToOptimize.title'),
            description: i18n.t('enforcement:nothingToOptimize.description'),
          });
          return;
        }

        // Experiment-Guard (409): auf dem Feed laeuft ein A/B-Test. Eine Optimierung
        // wuerde optimized_* der Treatment-Produkte mitten im Fenster ueberschreiben
        // und damit die Behandlung aendern (Gueltigkeitsbedingung V6 des Analyseplans).
        if (serverBody?.error === 'EXPERIMENT_RUNNING') {
          toast({
            title: i18n.t('common:status.error'),
            description: i18n.t('dashboard:toasts.optimizeExperimentRunning'),
            variant: 'destructive',
          });
          return;
        }

        // Guard-Ausfall (503): die Function konnte nicht feststellen, ob ein Experiment
        // laeuft, und hat fail-closed nichts eingereiht. Eigener Text, weil "gleich
        // nochmal" hier die richtige Handlung ist und nicht "Fehler melden".
        if (serverBody?.error === 'CONTROL_GUARD_UNAVAILABLE') {
          toast({
            title: i18n.t('common:status.error'),
            description: i18n.t('dashboard:toasts.optimizeGuardUnavailable'),
            variant: 'destructive',
          });
          return;
        }

        // Surface the backend error verbatim so we never get a "silent fail" again.
        const description =
          serverBody?.message
          ?? (error as any)?.context?.message
          ?? (error as Error)?.message
          ?? i18n.t('dashboard:toasts.optimizationFailed');
        toast({ title: i18n.t('common:status.error'), description, variant: 'destructive' });
        Sentry.captureException(error, { tags: { feature: 'optimization', action: 'enqueue' } });
        return;
      }

      const totalProducts = data?.total_products || 0;
      // Funnel-Event: der Server hat den Lauf angenommen (Job-Id liegt vor) —
      // alle 4xx/5xx-Pfade sind oben bereits ausgestiegen.
      trackConversion('optimization_started', { product_count: totalProducts });
      // Cross-Tab-Marker erst NACH der Server-Annahme schreiben: ein
      // abgelehnter Lauf darf andere Tabs nicht sperren.
      if (data?.job_id) {
        markOptimizationRunning(feedId, data.job_id);
      }
      setLocalJobInfo({ jobId: data?.job_id || '', totalProducts });
      setShowProgressModal(true);
      invalidate();
      if (data?.quota_capped && !confirmOpts?.confirmQuotaCap) {
        // Sicherheitsnetz: die Vorwarnung hier hat nicht gegriffen (Neu-Zahl
        // unbekannt, geschaetzt zu niedrig, Enforcement-Daten veraltet), der
        // Server hat die NEUEN Produkte aber auf die freie Kapazitaet gekuerzt.
        // Ehrlich benennen statt still durchlassen.
        //
        // Final-Review F-2: gemeldet werden die NEU-Zahlen der Serverantwort
        // (`requested_new` / `accepted_new` / `dropped_new`). Die alte Fassung
        // stellte `requested_products` — die GANZE Auswahl inklusive
        // Wiederholungen — der Restkapazitaet gegenueber und behauptete damit
        // eine Kuerzung an Zahlen, die verschiedene Grundmengen messen: bei 35
        // ausgewaehlten Produkten, davon 5 Wiederholungen, las der Toast "von
        // deinen 35 neuen Produkten".
        //
        // FALLBACK auf die alte Formulierung, wenn die drei Felder fehlen: der
        // Server kann ein aelterer Deploy sein (Edge Functions und Frontend
        // gehen getrennt live). Der Server setzt sie IMMER, auch mit 0 — ihre
        // Abwesenheit ist deshalb ein verlaesslicher Versions-Indikator.
        const acceptedNew = data.accepted_new;
        const requestedNew = data.requested_new;
        const droppedNew = data.dropped_new;
        const hasNewCounts =
          typeof acceptedNew === 'number' &&
          typeof requestedNew === 'number' &&
          typeof droppedNew === 'number';
        toast({
          title: i18n.t('enforcement:quotaCap.title'),
          description: hasNewCounts
            // `count` steuert die Pluralform und traegt die gedroppte Menge —
            // das ist die Zahl, um die es in dem Satz geht.
            ? i18n.t('enforcement:quotaCap.toastDescription', {
                count: droppedNew,
                acceptedNew,
                requestedNew,
                dropped: droppedNew,
                managed: data.managed_count ?? managedCount,
                limit: data.product_limit ?? planLimit,
              })
            // `remaining_quota` traegt im Erfolgs-Body dieselbe Zahl wie
            // `remaining_capacity` — der Name blieb fuer aeltere Builds erhalten.
            : i18n.t('enforcement:quotaCap.description', {
                requested: data.requested_products ?? totalProducts,
                remaining: data.remaining_quota ?? totalProducts,
                limit: data.product_limit ?? planLimit,
                used: data.managed_count ?? managedCount,
              }),
          variant: 'destructive',
        });
      } else {
        // `count` numerisch (Pluralwahl), `formatted` fuer die Anzeige — ein
        // String-count schaltet die i18next-Pluralformen ab ("1 Produkte").
        toast({ title: i18n.t('dashboard:toasts.optimizationStarted'), description: i18n.t('dashboard:toasts.productsQueued', { count: totalProducts, formatted: totalProducts.toLocaleString() }) });
      }
    } catch (error) {
      setLocalJobInfo(null);
      Sentry.captureException(error, { tags: { feature: 'optimization', action: 'start' } });
      toast({ title: i18n.t('common:status.error'), description: error instanceof Error ? error.message : i18n.t('dashboard:toasts.optimizationFailed'), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm handler for the "already optimized — sure?" dialog: re-run the same
  // optimization with the confirm flag so the server skips the signature gate.
  const confirmReoptimize = async () => {
    if (!pendingOptimizeOpts) return;
    const opts = pendingOptimizeOpts;
    setShowReoptimizeDialog(false);
    setPendingOptimizeOpts(null);
    await handleOptimize(opts, { confirm: true });
  };

  // Confirm handler for the "not enough quota" dialog: re-run the optimization
  // bypassing the quota pre-check. The server still trims to the remaining slots.
  const confirmQuotaCapOptimize = async () => {
    if (!pendingOptimizeOpts) return;
    const opts = pendingOptimizeOpts;
    setShowQuotaCapDialog(false);
    setPendingOptimizeOpts(null);
    await handleOptimize(opts, { confirmQuotaCap: true });
  };

  // Cancel handler — closes the modal, wipes any sticky completion snapshot,
  // engages the cleanup lock so the user cannot race a fresh optimize against
  // in-flight worker drain or reset-pending-products.
  const handleCancel = async () => {
    if (!feedId) return;
    cancelledRef.current = true;
    setCancelling(true);
    setCancelTriggered(true);
    setShowProgressModal(false);
    setHasCompletedInSession(false);
    setCompletedJobSnapshot(null);
    setLastJobStats({ completed: 0, failed: 0 });
    try {
      // trackedJobId als letzter Rueckgriff: waehrend eines transienten
      // Poll-Lochs (Befund 3) ist activeJob null und localJobInfo laengst
      // geraeumt — ohne den Rueckgriff verloere der Abbrechen-Weg seinen Job.
      const jobId = activeJob?.id || localJobInfo?.jobId || trackedJobId || undefined;
      let outcome: CancelJobResult | undefined;
      if (jobId) {
        outcome = await cancelJob(jobId);
      }
      // WP0/K01: cancelled false heisst, der Job war schon fertig. Dann nichts
      // zuruecksetzen (reset-pending-products koennte Produkte eines anderen,
      // gerade laufenden Jobs treffen) und ehrlich melden, dass nichts
      // abgebrochen wurde. `undefined` (kein Job gefunden) gilt wie bisher als
      // abgebrochen.
      const alreadyFinished = outcome?.cancelled === false;
      if (!alreadyFinished) {
        await supabase.functions.invoke('reset-pending-products', { body: { feed_id: feedId } });
      }
      setLocalJobInfo(null);
      setTrackedJobId(null);
      clearOptimizationRunMarker(feedId, jobId);
      toast({
        title: i18n.t(alreadyFinished ? 'dashboard:toasts.optimizationAlreadyFinished' : 'dashboard:toasts.optimizationCancelled'),
      });
    } catch (error) {
      console.error('Cancel optimization failed:', error);
      Sentry.captureException(error, { tags: { feature: 'optimization', action: 'cancel' } });
      toast({ title: i18n.t('common:status.error'), description: i18n.t('dashboard:toasts.cancelFailed'), variant: 'destructive' });
    } finally {
      setCancelling(false);
    }
  };

  // Release cancelTriggered once polling reports cleanup done.
  useEffect(() => {
    if (cancelTriggered && !cleanupLock.locked) {
      setCancelTriggered(false);
    }
  }, [cancelTriggered, cleanupLock.locked]);

  return {
    activeJob,
    isOptimizing,
    optimizationJustCompleted,
    scoreJobInfo,
    setScoreJobInfo,
    localJobInfo,
    trackedJobId,
    progressData,
    submitting,
    cancelling,
    lastJobStats,
    showProgressModal,
    setShowProgressModal,
    showUpgradeModal,
    setShowUpgradeModal,
    showReoptimizeDialog,
    setShowReoptimizeDialog,
    confirmReoptimize,
    reoptimizeDroppedNew,
    showQuotaCapDialog,
    setShowQuotaCapDialog,
    quotaCapInfo,
    confirmQuotaCapOptimize,
    currentPhase,
    phaseTimings,
    categoryCounts,
    preScore,
    heartbeatStaleSeconds,
    hasCompletedInSession,
    completedJobSnapshot,
    handleOptimize,
    handleCancel,
    cleanupLock,
  };
}
